// ConditionGroupBuilder — a reusable AND/OR/NOT rule builder. Domain-agnostic:
// it is fed `facets` (fields + operators + value kind), `options` (per-field
// pick lists) and `people` (for person-valued facets) and emits the exact JSON
// tree the backend accepts (see conditionGroup.ts). It never evaluates a rule;
// the only client-side logic is the structural validation and the sentence
// summary, both mirrored from the server. Learning uses it for audience rules;
// Surveys can adopt it as-is. All copy is overridable through `labels` so the
// caller decides the i18n namespace.
import React, { useMemo, useRef, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  type ConditionLeaf, type Facet, type FacetOption, type FacetPerson, type GroupNode, type NotNode, type RuleNode, type SentencePhrases, type ValidationIssue,
  MAX_DEPTH, editorKindFor, groupChildren, groupKind, isGroup, isNot, makeGroup, NO_VALUE_OPS, ruleToSentence, validateRule,
} from './conditionGroup';

export interface BuilderLabels {
  matchAll: string; matchAny: string; all: string; any: string; addCondition: string; addGroup: string; removeCondition: string; removeGroup: string;
  not: string; notUnavailable: string; field: string; operator: string; value: string; pickField: string; searchPeople: string; noMatches: string;
  filterOptions: string; days: string; summary: string; selected: string; remove: string; opLabels: Record<string, string>;
  issues: Partial<Record<ValidationIssue['code'], string>>; phrases: Partial<SentencePhrases>;
}
export const defaultBuilderLabels: BuilderLabels = {
  matchAll: 'Match all of the following', matchAny: 'Match any of the following', all: 'All', any: 'Any',
  addCondition: 'Add condition', addGroup: 'Add group', removeCondition: 'Remove condition', removeGroup: 'Remove group',
  not: 'NOT', notUnavailable: 'NOT is not available this deep', field: 'Field', operator: 'Operator', value: 'Value', pickField: 'Choose a field…',
  searchPeople: 'Search people…', noMatches: 'No matches', filterOptions: 'Filter…', days: 'days', summary: 'In plain words', selected: 'Selected', remove: 'Remove',
  opLabels: {
    in: 'is any of', not_in: 'is none of', eq: 'is', neq: 'is not', contains: 'contains', subtree_of: 'is anywhere under', reports_to: 'reports directly to',
    within_days: 'within the last … days', before: 'is before', after: 'is on or after', is_set: 'is set', is_not_set: 'is not set',
  },
  issues: {}, phrases: {},
};

export interface ConditionGroupBuilderProps {
  facets: Facet[];
  options: Record<string, FacetOption[]>;
  people: FacetPerson[];
  value: RuleNode | null;
  onChange: (next: RuleNode | null) => void;
  labels?: Partial<BuilderLabels>;
  disabled?: boolean;
  maxDepth?: number;
  /** Prefix for element ids (several builders on one page). */
  idPrefix?: string;
  className?: string;
  /** Hide the sentence + validation footer (caller renders its own). */
  hideSummary?: boolean;
}

const selectCls = 'h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground disabled:opacity-50';
const chipCls = (on: boolean) => `rounded-full border px-2.5 py-1 text-xs font-medium transition ${on ? 'border-primary-500 bg-primary-500/15 text-primary-700 dark:text-primary-300' : 'border-border text-muted-foreground hover:bg-muted'}`;

const asRoot = (v: RuleNode | null | undefined): GroupNode => (isGroup(v) ? v : v ? { all: [v] } : { all: [] });
const emptyLeaf = (facets: Facet[]): ConditionLeaf => {
  const f = facets[0];
  return { field: f?.field || '', op: f?.ops[0] || '' };
};

/** Build a leaf with its value AND the human label captured from what was picked. */
function buildLeaf(prev: ConditionLeaf, facet: Facet | undefined, op: string, raw: string | string[] | number | undefined, ctx: { options: FacetOption[]; people: FacetPerson[] }): ConditionLeaf {
  const leaf: ConditionLeaf = { field: prev.field, op };
  if (NO_VALUE_OPS.has(op) || raw === undefined || raw === '' || (Array.isArray(raw) && raw.length === 0)) return leaf;
  leaf.value = raw;
  const kind = editorKindFor(facet, op);
  const nameOf = (v: string) => ctx.people.find((p) => p.user_id === v)?.name || ctx.options.find((o) => o.value === v)?.label || v;
  switch (kind) {
    case 'multi': case 'people': case 'single': leaf.label = (Array.isArray(raw) ? raw : [String(raw)]).map(nameOf).join(', '); break;
    case 'person': leaf.label = `${op === 'reports_to' ? 'reports to' : 'reports up to'} ${nameOf(String(raw))}`; break;
    case 'number': leaf.label = `hired in the last ${raw} days`; break;
    case 'date': leaf.label = `${op === 'before' ? 'hired before' : 'hired on or after'} ${raw}`; break;
    default: leaf.label = String(raw);
  }
  return leaf;
}

export const ConditionGroupBuilder: React.FC<ConditionGroupBuilderProps> = ({
  facets, options, people, value, onChange, labels: labelsIn, disabled, maxDepth = MAX_DEPTH, idPrefix = 'cgb', className, hideSummary,
}) => {
  const L = useMemo<BuilderLabels>(() => ({ ...defaultBuilderLabels, ...(labelsIn || {}), opLabels: { ...defaultBuilderLabels.opLabels, ...(labelsIn?.opLabels || {}) } }), [labelsIn]);
  const root = asRoot(value);
  // Stable React keys per node object. Edits produce new objects, so the key
  // is INHERITED from the node being replaced — a row keeps its DOM (and focus
  // in a text input) while you type; siblings are untouched.
  const keys = useRef(new WeakMap<object, string>());
  const seq = useRef(0);
  const keyOf = (n: object) => { let k = keys.current.get(n); if (!k) { k = `n${seq.current++}`; keys.current.set(n, k); } return k; };
  const inherit = <T extends RuleNode | null>(from: RuleNode, to: T): T => { if (to && to !== from && !keys.current.has(to)) keys.current.set(to, keyOf(from)); return to; };

  const emit = (next: GroupNode) => onChange(groupChildren(next).length === 0 ? null : next);

  // Immutable path updates. path = child indexes from the root group.
  const replaceAt = (node: RuleNode, path: number[], fn: (n: RuleNode) => RuleNode | null): RuleNode | null => {
    if (path.length === 0) return inherit(node, fn(node));
    const [i, ...rest] = path;
    if (isGroup(node)) {
      const kids = groupChildren(node).slice();
      const r = replaceAt(kids[i], rest, fn);
      if (r === null) kids.splice(i, 1); else kids[i] = r;
      return inherit(node, makeGroup(groupKind(node), kids));
    }
    if (isNot(node)) {
      const r = replaceAt(node.not, rest, fn);
      return r === null ? null : inherit(node, { not: r });
    }
    return node;
  };
  const update = (path: number[], fn: (n: RuleNode) => RuleNode | null) => emit(asRoot(replaceAt(root, path, fn)));
  const append = (path: number[], child: RuleNode) => update(path, (n) => (isGroup(n) ? makeGroup(groupKind(n), [...groupChildren(n), child]) : n));

  const issues = useMemo(() => validateRule(value), [value]);
  const sentence = useMemo(() => ruleToSentence(value, facets, { options, people, phrases: L.phrases }), [value, facets, options, people, L.phrases]);

  const renderLeafRow = (node: RuleNode, path: number[], depth: number) => {
    const negated = isNot(node);
    const leaf = (negated ? (node as NotNode).not : node) as ConditionLeaf;
    const facet = facets.find((f) => f.field === leaf.field);
    const ops = facet?.ops || [];
    const kind = editorKindFor(facet, leaf.op);
    const fieldOptions = options[leaf.field] || [];
    const canNegate = negated || depth + 1 <= maxDepth;
    const id = `${idPrefix}-${path.join('-')}`;
    const ctx = { options: fieldOptions, people };
    const setLeaf = (next: ConditionLeaf) => update(path, () => (negated ? { not: next } : next));
    const setValue = (raw: string | string[] | number | undefined) => setLeaf(buildLeaf(leaf, facet, leaf.op, raw, ctx));
    const toggleNot = () => update(path, (n) => (isNot(n) ? n.not : { not: n }));
    const remove = () => update(path, () => null);

    return (
      <li key={keyOf(node)} className="rounded-md border border-border bg-background p-2 space-y-2" data-testid="cgb-row">
        <div className="flex flex-wrap items-center gap-1.5">
          <button type="button" aria-pressed={negated} disabled={disabled || !canNegate} title={canNegate ? L.not : L.notUnavailable}
            className={`h-8 rounded-md border px-2 text-xs font-semibold transition ${negated ? 'border-red-400 bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300' : 'border-border text-muted-foreground hover:bg-muted'} disabled:opacity-40`}
            onClick={toggleNot}>{L.not}</button>
          <label htmlFor={`${id}-field`} className="sr-only">{L.field}</label>
          <select id={`${id}-field`} className={selectCls} disabled={disabled} value={leaf.field}
            onChange={(e) => { const f = facets.find((x) => x.field === e.target.value); setLeaf({ field: e.target.value, op: f?.ops[0] || '' }); }}>
            {!leaf.field && <option value="">{L.pickField}</option>}
            {facets.map((f) => <option key={f.field} value={f.field}>{f.label}</option>)}
          </select>
          <label htmlFor={`${id}-op`} className="sr-only">{L.operator}</label>
          <select id={`${id}-op`} className={selectCls} disabled={disabled || !facet} value={leaf.op}
            onChange={(e) => {
              const nextOp = e.target.value;
              const sameKind = editorKindFor(facet, nextOp) === kind;
              setLeaf(buildLeaf(leaf, facet, nextOp, sameKind ? leaf.value : undefined, ctx));
            }}>
            {ops.map((op) => <option key={op} value={op}>{L.opLabels[op] || op}</option>)}
          </select>
          {kind === 'single' && (
            <>
              <label htmlFor={`${id}-value`} className="sr-only">{L.value}</label>
              <select id={`${id}-value`} className={selectCls} disabled={disabled} value={typeof leaf.value === 'string' ? leaf.value : ''} onChange={(e) => setValue(e.target.value || undefined)}>
                <option value="">{L.value}…</option>
                {fieldOptions.map((o) => <option key={o.value} value={o.value}>{o.label}{typeof o.count === 'number' ? ` (${o.count})` : ''}</option>)}
              </select>
            </>
          )}
          {kind === 'text' && (
            <>
              <label htmlFor={`${id}-value`} className="sr-only">{L.value}</label>
              <Input id={`${id}-value`} className="h-8 w-48" disabled={disabled} value={typeof leaf.value === 'string' ? leaf.value : ''} onChange={(e) => setValue(e.target.value || undefined)} />
            </>
          )}
          {kind === 'number' && (
            <>
              <label htmlFor={`${id}-value`} className="sr-only">{L.value}</label>
              <Input id={`${id}-value`} type="number" min={1} step={1} className="h-8 w-24" disabled={disabled} value={typeof leaf.value === 'number' ? leaf.value : (leaf.value as string | undefined) ?? ''}
                onChange={(e) => setValue(e.target.value === '' ? undefined : Number(e.target.value))} />
              <span className="text-xs text-muted-foreground">{L.days}</span>
            </>
          )}
          {kind === 'date' && (
            <>
              <label htmlFor={`${id}-value`} className="sr-only">{L.value}</label>
              <Input id={`${id}-value`} type="date" className="h-8 w-40" disabled={disabled} value={typeof leaf.value === 'string' ? leaf.value : ''} onChange={(e) => setValue(e.target.value || undefined)} />
            </>
          )}
          <button type="button" aria-label={L.removeCondition} disabled={disabled} className="ml-auto text-muted-foreground hover:text-red-500 disabled:opacity-40" onClick={remove}><Trash2 className="h-4 w-4" /></button>
        </div>
        {kind === 'multi' && (
          <OptionChips id={`${id}-value`} options={fieldOptions} value={Array.isArray(leaf.value) ? leaf.value.map(String) : []} onChange={(v) => setValue(v)} disabled={disabled} labels={L} />
        )}
        {(kind === 'person' || kind === 'people') && (
          <PersonPicker id={`${id}-value`} people={people} multiple={kind === 'people'} disabled={disabled} labels={L}
            value={Array.isArray(leaf.value) ? leaf.value.map(String) : typeof leaf.value === 'string' && leaf.value ? [leaf.value] : []}
            onChange={(ids) => setValue(kind === 'people' ? ids : ids[0])} />
        )}
      </li>
    );
  };

  const renderGroup = (node: RuleNode, path: number[], depth: number): React.ReactNode => {
    const negated = isNot(node);
    const group = (negated ? (node as NotNode).not : node) as GroupNode;
    const kind = groupKind(group);
    const kids = groupChildren(group);
    const isRoot = path.length === 0;
    const canAddGroup = depth + 2 <= maxDepth;
    // NOT over a group adds a level above every leaf inside it.
    const canNegate = !isRoot && (negated || depth + 2 <= maxDepth);
    const setKind = (k: 'all' | 'any') => update(path, (n) => {
      const g = (isNot(n) ? n.not : n) as GroupNode;
      const next = makeGroup(k, groupChildren(g));
      return isNot(n) ? { not: next } : next;
    });
    const leafDepth = depth + 1;
    return (
      <div key={isRoot ? 'root' : keyOf(node)} className={isRoot ? 'space-y-2' : 'rounded-md border border-dashed border-border bg-muted/40 p-2 space-y-2'} data-testid={isRoot ? 'cgb-root' : 'cgb-group'} data-depth={depth}>
        <div className="flex flex-wrap items-center gap-1.5">
          {!isRoot && canNegate && (
            <button type="button" aria-pressed={negated} disabled={disabled} title={L.not}
              className={`h-7 rounded-md border px-2 text-[11px] font-semibold transition ${negated ? 'border-red-400 bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300' : 'border-border text-muted-foreground hover:bg-muted'} disabled:opacity-40`}
              onClick={() => update(path, (n) => (isNot(n) ? n.not : { not: n }))}>{L.not}</button>
          )}
          <div role="group" aria-label={isRoot ? L.matchAll : L.all} className="inline-flex rounded-md border border-border overflow-hidden">
            {(['all', 'any'] as const).map((k) => (
              <button key={k} type="button" aria-pressed={kind === k} disabled={disabled}
                className={`px-2.5 h-7 text-xs font-medium transition ${kind === k ? 'bg-primary-500 text-white' : 'bg-background text-muted-foreground hover:bg-muted'}`}
                onClick={() => setKind(k)}>
                {isRoot ? (k === 'all' ? L.matchAll : L.matchAny) : (k === 'all' ? L.all : L.any)}
              </button>
            ))}
          </div>
          {!isRoot && <button type="button" aria-label={L.removeGroup} disabled={disabled} className="ml-auto text-muted-foreground hover:text-red-500 disabled:opacity-40" onClick={() => update(path, () => null)}><X className="h-4 w-4" /></button>}
        </div>
        <ul className="space-y-2">
          {kids.map((child, i) => {
            const childPath = [...path, i];
            const inner = isNot(child) ? child.not : child;
            return isGroup(inner) ? <li key={keyOf(child)} className="list-none">{renderGroup(child, childPath, leafDepth)}</li> : renderLeafRow(child, childPath, leafDepth);
          })}
        </ul>
        <div className="flex flex-wrap gap-1.5">
          <Button type="button" size="sm" variant="outline" disabled={disabled || facets.length === 0} onClick={() => append(path, emptyLeaf(facets))}><Plus className="mr-1 h-3.5 w-3.5" />{L.addCondition}</Button>
          {canAddGroup && <Button type="button" size="sm" variant="ghost" disabled={disabled || facets.length === 0} onClick={() => append(path, makeGroup(kind === 'all' ? 'any' : 'all', [emptyLeaf(facets)]))}><Plus className="mr-1 h-3.5 w-3.5" />{L.addGroup}</Button>}
        </div>
      </div>
    );
  };

  return (
    <div className={className} data-testid="condition-group-builder">
      {renderGroup(root, [], 1)}
      {!hideSummary && (
        <div className="mt-2 space-y-1">
          <p className="text-xs text-muted-foreground" data-testid="cgb-sentence"><span className="font-medium text-foreground">{L.summary}:</span> {sentence}</p>
          {value && issues.length > 0 && (
            <ul aria-live="polite" className="text-xs text-amber-700 dark:text-amber-300 list-disc pl-4" data-testid="cgb-issues">
              {issues.slice(0, 5).map((i, n) => <li key={`${i.code}-${i.path.join('.')}-${n}`}>{L.issues[i.code] || i.message}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ---- value editors ----------------------------------------------------------
const OptionChips: React.FC<{ id: string; options: FacetOption[]; value: string[]; onChange: (v: string[]) => void; disabled?: boolean; labels: BuilderLabels }> = ({ id, options, value, onChange, disabled, labels }) => {
  const [q, setQ] = useState('');
  const shown = useMemo(() => (q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options), [options, q]);
  return (
    <div className="space-y-1.5" role="group" aria-label={labels.value}>
      {options.length > 12 && <Input aria-label={labels.filterOptions} className="h-7 max-w-xs" placeholder={labels.filterOptions} value={q} onChange={(e) => setQ(e.target.value)} disabled={disabled} />}
      <div className="flex flex-wrap gap-1.5" id={id}>
        {shown.slice(0, 200).map((o) => {
          const on = value.includes(o.value);
          return (
            <button key={o.value} type="button" aria-pressed={on} disabled={disabled} className={chipCls(on)} onClick={() => onChange(on ? value.filter((x) => x !== o.value) : [...value, o.value])}>
              {o.label}{typeof o.count === 'number' ? <span className="ml-1 opacity-70">{o.count}</span> : null}
            </button>
          );
        })}
        {shown.length === 0 && <span className="text-xs text-muted-foreground">{labels.noMatches}</span>}
      </div>
    </div>
  );
};

/**
 * Searchable people picker (type-ahead with visible suggestions, chips for the
 * chosen people, an explicit "no matches" row). Shared by the rule builder and by
 * any surface that picks people from the same facet roster (champions, etc.).
 */
export const PersonPicker: React.FC<{ id: string; people: FacetPerson[]; value: string[]; multiple: boolean; onChange: (ids: string[]) => void; disabled?: boolean; labels?: Partial<BuilderLabels>; className?: string }> = ({ id, people, value, multiple, onChange, disabled, labels: labelsIn, className }) => {
  const labels = useMemo<BuilderLabels>(() => ({ ...defaultBuilderLabels, ...(labelsIn || {}) }), [labelsIn]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const picked = value.map((v) => people.find((p) => p.user_id === v) || { user_id: v, name: v });
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return people.filter((p) => !value.includes(p.user_id)).filter((p) => !needle || p.name.toLowerCase().includes(needle) || (p.department || '').toLowerCase().includes(needle)).slice(0, 30);
  }, [people, value, q]);
  const pick = (uid: string) => { onChange(multiple ? [...value, uid] : [uid]); setQ(''); setOpen(multiple); };
  return (
    <div className="space-y-1.5">
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1.5" aria-label={labels.selected}>
          {picked.map((p) => (
            <span key={p.user_id} className="inline-flex items-center gap-1 rounded-full border border-primary-500 bg-primary-500/15 px-2.5 py-1 text-xs font-medium text-primary-700 dark:text-primary-300">
              {p.name}
              <button type="button" aria-label={`${labels.remove} ${p.name}`} disabled={disabled} className="rounded-full hover:text-red-500 disabled:opacity-40" onClick={() => onChange(value.filter((x) => x !== p.user_id))}><X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      {(multiple || picked.length === 0) && (
        <div className={`relative ${className || 'max-w-sm'}`}>
          <Input id={id} role="combobox" aria-expanded={open} aria-controls={`${id}-list`} aria-autocomplete="list" className="h-8" placeholder={labels.searchPeople} value={q} disabled={disabled}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)}
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); if (e.key === 'Enter' && results[0]) { e.preventDefault(); pick(results[0].user_id); } }} />
          {open && (
            <ul id={`${id}-list`} role="listbox" className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md text-sm">
              {results.map((p) => (
                <li key={p.user_id} role="option" aria-selected={false}>
                  <button type="button" className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left hover:bg-muted" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(p.user_id)}>
                    <span className="truncate">{p.name}</span>{p.department ? <span className="text-xs text-muted-foreground truncate">{p.department}</span> : null}
                  </button>
                </li>
              ))}
              {results.length === 0 && <li className="px-2 py-1.5 text-xs text-muted-foreground">{labels.noMatches}</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ConditionGroupBuilder;
